class TopBar extends HTMLElement {
  constructor() {
    super();

    const config = this._getConfig();

    this.cookieName = config.cookieName || 'topbar';
    this.heightVar = config.heightVar || '--top-bar-section-height';

    this.mobileMode = config.mobileMode || 'horizontal';
    this.desktopMode = config.desktopMode || 'horizontal';

    this.mobileSpeed = this._parsePositiveInt(config.mobileSpeed, 5);
    this.desktopSpeed = this._parsePositiveInt(config.desktopSpeed, 30);

    this.matchThemeColor = config.matchThemeColor === true;
    this.themeColor = config.themeColor || '';

    this.intervalId = null;
    this.resizeObserver = null;
    this.verticalIndex = 1;
    this.verticalStep = 1;
    this.verticalTransitionMs = 300;

    this.onViewportChangeHandler = this._onViewportChange.bind(this);
    this.onResizeHandler = this._onResize.bind(this);
    this.onVerticalTransitionEndHandler = this._onVerticalTransitionEnd.bind(this);
  }

  connectedCallback() {
    if (cookies.getCookie(this.cookieName)) {
      this._closeTopBar({ persist: false });
      return;
    }

    this.closeButton = this.querySelector('.top-bar__close');
    this.marquee = this.querySelector('[data-top-bar-marquee]');
    this.track = this.querySelector('[data-track]');
    this.messages = Array.from(this.querySelectorAll('.top-bar-message'));

    if (!this.track || this.messages.length === 0) {
      this._closeTopBar({ persist: false });
      return;
    }

    this._bindEvents();
    this._setThemeColor();
    this._applyMode();
    this._syncHeightVars();
    this._observeHeight();

    window.requestAnimationFrame(() => {
      if (this.isConnected) {
        this.setAttribute('data-ready', 'true');
      }
    });
  }

  disconnectedCallback() {
    this._teardownMode();

    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.handleCloseClick);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    } else {
      window.removeEventListener('resize', this.onResizeHandler);
    }

    window.removeEventListener('resize', this.onViewportChangeHandler);
  }

  _bindEvents() {
    this.handleCloseClick = () => this._closeTopBar({ persist: true });

    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.handleCloseClick);
    }

    window.addEventListener('resize', this.onViewportChangeHandler);
  }

  _onViewportChange() {
    this._applyMode();

    if (this.dataset.mode === 'horizontal') {
      this._fillHorizontalMarquee();
    }

    this._syncHeightVars();
  }

  _onResize() {
    if (this.dataset.mode === 'vertical') {
      this._setVerticalPosition(this.verticalIndex, false);
    }

    if (this.dataset.mode === 'horizontal') {
      this._fillHorizontalMarquee();
    }

    this._syncHeightVars();
  }

  _getCurrentConfig() {
    const desktop = window.matchMedia('(min-width: 1024px)').matches;

    return {
      mode: desktop ? this.desktopMode : this.mobileMode,
      speed: desktop ? this.desktopSpeed : this.mobileSpeed,
    };
  }

  _applyMode() {
    const config = this._getCurrentConfig();
    const mode = config.mode === 'static' || config.mode === 'vertical' ? config.mode : 'horizontal';

    if (this.activeMode === mode && this.activeSpeed === config.speed) {
      return;
    }

    this._teardownMode();

    this.activeMode = mode;
    this.activeSpeed = config.speed;

    this.dataset.mode = mode;

    if (mode === 'horizontal') {
      this._applyHorizontalMode(config);
      return;
    }

    if (mode === 'vertical') {
      this._applyVerticalMode(config);
      return;
    }

    this._applyStaticMode();
  }

  _applyHorizontalMode(config) {
    if (!this.marquee || !this.track) return;

    this.marquee.removeAttribute('disabled');
    this.marquee.setAttribute('duration-ms', String(config.speed));
    this.marquee.setAttribute('speed', '1');
    this.marquee.removeAttribute('reversed');

    this._fillHorizontalMarquee();

    // Nudge the marquee component to recompute duration after cloning.
    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }

  _applyVerticalMode(config) {
    if (this.marquee) {
      this.marquee.setAttribute('disabled', '');
    }

    this._setupVerticalTrack();
    this._setVerticalPosition(this.verticalIndex, false);

    if (this.messages.length <= 1) return;

    this.verticalStep = 1;
    this.verticalTransitionMs = Math.min(700, Math.max(220, Math.round(config.speed * 0.25)));

    this.intervalId = window.setInterval(
      () => {
        this._setVerticalPosition(this.verticalIndex + this.verticalStep, true);
      },
      Math.max(config.speed, 1000),
    );
  }

  _applyStaticMode() {
    if (this.marquee) {
      this.marquee.setAttribute('disabled', '');
    }

    if (this.track) {
      this.track.style.transform = '';
      this.track.style.transition = '';
    }

    const viewport = this.querySelector('[data-messages]');
    if (viewport) {
      viewport.style.height = '';
    }
  }

  _teardownMode() {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.track) {
      this.track.removeEventListener('transitionend', this.onVerticalTransitionEndHandler);
      this.removeClones();
      this.track.style.transform = '';
      this.track.style.transition = '';
    }

    if (this.marquee) {
      this.marquee.removeAttribute('reversed');
      this.marquee.removeAttribute('duration-ms');
    }

    const viewport = this.querySelector('[data-messages]');
    if (viewport) {
      viewport.style.height = '';
    }
  }

  _observeHeight() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this._syncHeightVars());
      this.resizeObserver.observe(this);
      return;
    }

    window.addEventListener('resize', this.onResizeHandler);
  }

  _syncHeightVars() {
    if (!document.body || !this.isConnected) return;

    const height = `${this.clientHeight}px`;
    document.body.style.setProperty(this.heightVar, height);
  }

  _setThemeColor() {
    if (!this.matchThemeColor) return;

    const themeColor = this.themeColor;
    if (!themeColor) return;

    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }

    meta.setAttribute('content', themeColor);
  }

  _closeTopBar({ persist }) {
    if (document.body) {
      document.body.style.setProperty(this.heightVar, '0px');
    }

    if (persist) {
      cookies.setCookie(this.cookieName, 'false', 1);
    }

    this.remove();
  }

  _parsePositiveInt(value, fallback) {
    const parsed = parseInt(value || '', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  _getConfig() {
    const el = this.querySelector('script[data-top-bar-config]');
    if (!el) return {};

    try {
      return JSON.parse(el.textContent || '{}');
    } catch (error) {
      console.error('Invalid top bar config JSON', error);
      return {};
    }
  }

  _setupVerticalTrack() {
    if (!this.track) return;

    const first = this.messages[0];
    const last = this.messages[this.messages.length - 1];

    if (!first || !last) return;

    const firstClone = first.cloneNode(true);
    const lastClone = last.cloneNode(true);

    firstClone.dataset.clone = 'true';
    firstClone.setAttribute('aria-hidden', 'true');

    lastClone.dataset.clone = 'true';
    lastClone.setAttribute('aria-hidden', 'true');

    this.track.insertBefore(lastClone, this.track.firstChild);
    this.track.appendChild(firstClone);
    this.track.addEventListener('transitionend', this.onVerticalTransitionEndHandler);
    this.verticalIndex = 1;
  }

  _setVerticalPosition(index, animate) {
    if (!this.track) return;

    this.verticalIndex = index;
    this.track.style.transition = animate ? `transform ${this.verticalTransitionMs}ms ease` : 'none';
    this.track.style.transform = `translate3d(0, ${-this.verticalIndex * 100}%, 0)`;
  }

  _onVerticalTransitionEnd(event) {
    if (event.propertyName !== 'transform' || this.dataset.mode !== 'vertical') return;
    if (!this.messages.length) return;

    if (this.verticalIndex === this.messages.length + 1) {
      this._setVerticalPosition(1, false);
      return;
    }

    if (this.verticalIndex === 0) {
      this._setVerticalPosition(this.messages.length, false);
    }
  }

  _fillHorizontalMarquee() {
    if (!this.marquee || !this.track) return;

    this.removeClones();

    const baseWidth = Math.ceil(this.track.getBoundingClientRect().width);
    const containerWidth = Math.ceil(this.marquee.getBoundingClientRect().width);

    if (!baseWidth || !containerWidth) return;

    const computed = getComputedStyle(this.marquee);
    const gap = parseFloat(computed.gap) || parseFloat(computed.columnGap) || 0;

    const minCoverage = containerWidth + baseWidth + gap;
    let coverage = baseWidth;
    let safety = 0;

    while (coverage < minCoverage && safety < 12) {
      const clone = this.track.cloneNode(true);
      clone.dataset.clone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      this.marquee.appendChild(clone);
      coverage += baseWidth + gap;
      safety += 1;
    }
  }

  removeClones() {
    this.querySelectorAll('[data-clone="true"]').forEach((clone) => clone.remove());
  }
}

customElements.define('top-bar', TopBar);
